import { createSlice } from "@reduxjs/toolkit";
import {
  createFraseSeleccionAplicacionPlantelThunk,
  getFraseSeleccionAplicacionPlantelByIdThunk,
  getFrasesSeleccionAplicacionPlantelThunk,
  toggleFraseSeleccionAplicacionPlantelActiveThunk,
  updateFraseSeleccionAplicacionPlantelThunk,
} from "./frasesSeleccionAplicacionPlantel.thunks";

const initialState = {
  list: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  count: 0,
  next: null,
  previous: null,
  maxPageReached: 1,
  search: "",

  selectedFrase: null,
  selectedFraseLoading: false,
  selectedFraseError: null,
  viewModalOpen: false,

  formModalOpen: false,
  formMode: "edit",
  formLoading: false,
  formError: null,

  confirmModalOpen: false,
  toggleFraseId: null,
  toggling: false,
};

const frasesSeleccionAplicacionPlantelSlice = createSlice({
  name: "frasesSeleccionAplicacionPlantel",
  initialState,
  reducers: {
    clearFrasesSeleccionAplicacionPlantelError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    resetFrasesSeleccionAplicacionPlantel: () => initialState,
    openViewModal: (state) => {
      state.viewModalOpen = true;
    },
    closeViewModal: (state) => {
      state.viewModalOpen = false;
      state.selectedFrase = null;
    },
    openFormModal: (state, action) => {
      state.formModalOpen = true;
      state.formMode = action.payload?.mode || "edit";
      if (action.payload?.frase) {
        state.selectedFrase = action.payload.frase;
      } else if (state.formMode === "create") {
        state.selectedFrase = null;
      }
      state.formError = null;
    },
    closeFormModal: (state) => {
      state.formModalOpen = false;
      state.selectedFrase = null;
      state.formError = null;
    },
    openConfirmToggle: (state, action) => {
      state.confirmModalOpen = true;
      state.toggleFraseId = action.payload.id;
    },
    closeConfirmToggle: (state) => {
      state.confirmModalOpen = false;
      state.toggleFraseId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getFrasesSeleccionAplicacionPlantelThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getFrasesSeleccionAplicacionPlantelThunk.fulfilled,
        (state, action) => {
          const { results, count, next, previous } = action.payload;
          state.loading = false;
          state.list = results;
          state.count = count;
          state.next = next;
          state.previous = previous;
          state.maxPageReached = Math.max(state.maxPageReached, state.page);
        },
      )
      .addCase(
        getFrasesSeleccionAplicacionPlantelThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      )

      // GET BY ID
      .addCase(getFraseSeleccionAplicacionPlantelByIdThunk.pending, (state) => {
        state.selectedFraseLoading = true;
        state.selectedFraseError = null;
      })
      .addCase(
        getFraseSeleccionAplicacionPlantelByIdThunk.fulfilled,
        (state, action) => {
          state.selectedFraseLoading = false;
          state.selectedFrase = action.payload;
        },
      )
      .addCase(
        getFraseSeleccionAplicacionPlantelByIdThunk.rejected,
        (state, action) => {
          state.selectedFraseLoading = false;
          state.selectedFraseError = action.payload;
        },
      )

      // CREATE
      .addCase(createFraseSeleccionAplicacionPlantelThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(
        createFraseSeleccionAplicacionPlantelThunk.fulfilled,
        (state) => {
          state.formLoading = false;
          state.formModalOpen = false;
        },
      )
      .addCase(
        createFraseSeleccionAplicacionPlantelThunk.rejected,
        (state, action) => {
          state.formLoading = false;
          state.formError = action.payload;
        },
      )

      // UPDATE
      .addCase(updateFraseSeleccionAplicacionPlantelThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(
        updateFraseSeleccionAplicacionPlantelThunk.fulfilled,
        (state, action) => {
          state.formLoading = false;
          const index = state.list.findIndex((f) => f.id === action.payload.id);
          if (index !== -1) state.list[index] = action.payload;
          state.formModalOpen = false;
        },
      )
      .addCase(
        updateFraseSeleccionAplicacionPlantelThunk.rejected,
        (state, action) => {
          state.formLoading = false;
          state.formError = action.payload;
        },
      )

      // TOGGLE ACTIVE
      .addCase(
        toggleFraseSeleccionAplicacionPlantelActiveThunk.pending,
        (state) => {
          state.toggling = true;
          // Optimistic update
          const index = state.list.findIndex(
            (f) => f.id === state.toggleFraseId,
          );
          if (index !== -1) {
            state.list[index] = {
              ...state.list[index],
              is_active: !state.list[index].is_active,
            };
          }
        },
      )
      .addCase(
        toggleFraseSeleccionAplicacionPlantelActiveThunk.fulfilled,
        (state, action) => {
          state.toggling = false;
          state.confirmModalOpen = false;
          const index = state.list.findIndex((f) => f.id === action.payload.id);
          if (index !== -1) state.list[index] = action.payload;
        },
      )
      .addCase(
        toggleFraseSeleccionAplicacionPlantelActiveThunk.rejected,
        (state, action) => {
          state.toggling = false;
          state.error = action.payload;
          // Revertir optimistic update
          const index = state.list.findIndex(
            (f) => f.id === state.toggleFraseId,
          );
          if (index !== -1) {
            state.list[index] = {
              ...state.list[index],
              is_active: !state.list[index].is_active,
            };
          }
        },
      );
  },
});

export const {
  clearFrasesSeleccionAplicacionPlantelError,
  setPage,
  setSearch,
  resetFrasesSeleccionAplicacionPlantel,
  openViewModal,
  closeViewModal,
  openFormModal,
  closeFormModal,
  openConfirmToggle,
  closeConfirmToggle,
} = frasesSeleccionAplicacionPlantelSlice.actions;

export default frasesSeleccionAplicacionPlantelSlice.reducer;
